/**
 * RestoID — MFA passkey (WebAuthn) binding.
 * ---------------------------------------------------------------------------
 * RestoID frame (hero + concise copy) around the PRISTINE upstream WebAuthn
 * binding logic (`useWebAuthnOperation`, verification-id lookup, skip) and the
 * shared RestoID `MfaSwitchLink`. Replaces Logto's verbose default copy.
 * Swapped in for `./pages/MfaBinding/WebAuthnBinding` via `src/App.tsx`.
 */
import { VerificationType } from '@logto/schemas';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { validate } from 'superstruct';

import FirstScreenLayout from '@/Layout/FirstScreenLayout';
import UserInteractionContext from '@/Providers/UserInteractionContextProvider/UserInteractionContext';
import useNavigateWithPreservedSearchParams from '@/hooks/use-navigate-with-preserved-search-params';
import useSkipMfa from '@/hooks/use-skip-mfa';
import useWebAuthnOperation from '@/hooks/use-webauthn-operation';
import { UserMfaFlow } from '@/types';
import { webAuthnStateGuard } from '@/types/guard';
import { isWebAuthnOptions } from '@/utils/webauthn';

import { MfaSwitchLink } from '../components/MfaFactorRows';
import { Button, GhostAction, HeroTile, ScreenHead, Stack } from '../components/restoid';
import { useCopy } from '../copy';

import ErrorPage from './ErrorPage';

const MfaWebAuthnBinding = () => {
  const { t } = useTranslation();
  const c = useCopy();
  const navigate = useNavigateWithPreservedSearchParams();
  const { state } = useLocation();
  const [, webAuthnState] = validate(state, webAuthnStateGuard);
  const { verificationIdsMap } = useContext(UserInteractionContext);
  const verificationId = verificationIdsMap[VerificationType.WebAuthn];

  const handleWebAuthn = useWebAuthnOperation();
  const skipMfa = useSkipMfa();
  const [isCreatingPasskey, setIsCreatingPasskey] = useState(false);

  if (!webAuthnState || !verificationId) {
    return <ErrorPage title="error.invalid_session" />;
  }

  const { options, availableFactors, skippable } = webAuthnState;

  if (!isWebAuthnOptions(options)) {
    return <ErrorPage title="error.invalid_session" />;
  }

  return (
    <FirstScreenLayout pageMeta={{ titleKey: 'mfa.create_a_passkey' }}>
      <Stack gap={22}>
        <ScreenHead
          back={() => navigate(-1)}
          backLabel={c('backShort')}
          title={c('mfaPasskeyTitle')}
          sub={c('mfaPasskeyDesc')}
        />
        <HeroTile icon="finger" />
        <Button
          title={c('mfaPasskeyCta')}
          isLoading={isCreatingPasskey}
          onClick={async () => {
            setIsCreatingPasskey(true);
            await handleWebAuthn(options, verificationId);
            setIsCreatingPasskey(false);
          }}
        />
        <MfaSwitchLink flow={UserMfaFlow.MfaBinding} flowState={{ availableFactors, skippable }} />
        {skippable ? (
          <GhostAction onClick={() => skipMfa()}>{t('action.nav_skip')}</GhostAction>
        ) : null}
      </Stack>
    </FirstScreenLayout>
  );
};

export default MfaWebAuthnBinding;
